import { COLORS } from './format';
import { useNotes } from './NotesContext';

/**
 * The 📝 notes chip for a candidate card.
 *
 * It renders on EVERY card, with or without notes (Theresa, Sep 2026). It used to self-hide when
 * the candidate had none, which meant the one candidate you most want to write something about —
 * the one nobody has written about yet — was the one with nowhere to write it. Adding a first note
 * meant opening Lever. The empty chip is deliberately quieter than the count: a card with notes
 * should still stand out at a glance.
 *
 * Clicking opens the shared notes modal (list + add), which is where a new note is written back to
 * Lever. Drop it onto any card: `<NotesChip candidateName={…} opportunityId={…} />`.
 *
 * Outside a NotesProvider it renders nothing at all — there is no modal for it to open, and a chip
 * that cannot be clicked is worse than no chip, which is exactly the risk of always rendering one.
 */
export function NotesChip({
  candidateName,
  opportunityId,
}: {
  candidateName: string;
  opportunityId: string;
}) {
  const notesApi = useNotes();
  if (!notesApi) return null;
  return (
    <button
      type="button"
      onClick={() => notesApi.openNotes({ opportunityId, candidateName })}
      className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold hover:opacity-80"
      style={{ backgroundColor: COLORS.cotton, color: '#767676' }}
      title="Read or add notes"
      aria-label={`Notes for ${candidateName}`}
    >
      📝
    </button>
  );
}
